import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import Toggle from 'react-bootstrap-toggle';
import { filter, findIndex, map, difference, reduce, indexOf } from 'lodash';
import { Button, Modal } from 'react-bootstrap';
import Fa from 'react-fontawesome';
import { Link } from 'react-router-dom';
import ProgressBar from '../../../../components/progress-bar';
// Import Actions
import { requestFetchProductFields, requestUpdateProductFields } from '../../SetupActions';
import { fetchAllFilters } from '../../FilterActions';

// Import Selectors
import { getAllProductFields } from '../../SetupReducer';
import { getUserCurrentOrganization } from '../../../Auth/AuthReducer';
import { getViewableDimensions } from '../../../App/AppReducer';
import CRMInstanceName from '../../../../components/crm-instance-name';
import SearchPath from '../../../../components/search-path';
import PathDisplay from '../../../../components/search-path/path';
import ConfirmButton from '../../../../components/confirm-button';

class ProductPage extends Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      loaded: false,
      fields: [],
      useProductTable: false,
      originalUseProductTable: false,
      originalFields: [],
      dirty: false,
      showModal: false,
      submitting: false,
    };
    
    this.loadCustomFields = this.loadCustomFields.bind(this);
    this.removeField = this.removeField.bind(this);
    // this.submit = this.submit.bind(this);
    this.hideModal = this.hideModal.bind(this);
    this.showModal = this.showModal.bind(this);
    this.handleSelectedSearchPath = this.handleSelectedSearchPath.bind(this);
    this.filterNodes = this.filterNodes.bind(this);
    this.onToggleStandardProduct = this.onToggleStandardProduct.bind(this);
    this.submit = this.submit.bind(this);
  }

  componentDidMount() {
    this.loadCustomFields();
  }

  showModal() {
    this.setState({ showModal: true });
  }

  hideModal() {
    this.setState({ showModal: false });
  }

  onToggleStandardProduct() {
    this.setState({ useProductTable: !this.state.useProductTable }, () => {
      this.checkDirty();
    });
  }

  checkDirty() {
    const { 
      fields, originalFields,
      useProductTable, originalUseProductTable,
    } = this.state;
    const originalIds = map(originalFields, 'field_id');
    const currentIds = map(fields, 'field_id');
    const removedIds = difference(originalIds, currentIds);
    const addedIds = difference(currentIds, originalIds);
    this.setState({
      dirty: removedIds.length !== 0 || addedIds.length !== 0 || useProductTable !== originalUseProductTable,
    });
  }

  handleSelectedSearchPath(path, info) {
    const exists = findIndex(this.state.fields, (f) => {
      return f.path?.join('::') === path.join('::');
    });
    if (exists < 0) {
      this.setState({
        showModal: false,
        fields: [
          ...this.state.fields,
          {
            field_id: path.join('::'),
            path,
            ...info,
            accessible: true, // important
          },
        ],
      }, () => {
        this.checkDirty();
      });
    } else {
      // already exist
      this.setState({
        showModal: false,
      }, () => {
        // TODO: show error
      });
    }
  }

  submit() {
    if (!this.state.submitting) {
      const { fields, useProductTable } = this.state;
      this.setState({ submitting: true }, () => {
        const productFields = map(filter(fields, { accessible: true }), 'path');
        this.props.dispatch(requestUpdateProductFields({ product_fields: productFields, use_product_table: useProductTable }))
          .then(
            (results) => {
              this.setState({
                submitting: false,
                fields: results.fields,
                useProductTable: results.use_product_table === true,
                originalUseProductTable: results.use_product_table === true,
                originalFields: [...results.fields],
                dirty: false,
              }, () => {
                // success
                const el = (<div>Custom Product Updated!</div>);
                this.context.addNotification(el, { type: 'success', autoClose: true });
              });
            },
            ({ data: { message } }) => {
              this.setState({ submitting: false }, () => {
                const el = (<div>{message}</div>);
                this.context.addNotification(el, { type: 'error', autoClose: false });
              });
            },
          );
      });
    }
  }

  removeField(field) {
    const fields = filter(this.state.fields, (f) => {
      return f.field_id !== field.field_id;
    });
    this.setState({
      fields,
    }, () => {
      this.checkDirty();
    });
  }


  loadCustomFields() {
    this.setState({
      loading: true, loaded: false, fields: [],
    });

    return new Promise((resolve, reject) => {
      const p1 = this.props.dispatch(fetchAllFilters());
      const p2 = this.props.dispatch(requestFetchProductFields());
      const allFieldsPromise = Promise.all([p1, p2]);
      allFieldsPromise.then(
        ([results1, results2]) => {
          const { fields } = results2;
          this.setState({
            loading: false,
            loaded: true,
            fields,
            useProductTable: results2.use_product_table === true,
            originalUseProductTable: results2.use_product_table === true,
            originalFields: [...fields],
          });
          resolve(results2);
        },
        (e) => {
          this.setState({
            loading: false, loaded: false, fields: [],
          });
          reject(e);
        },
      );
    });
  }

  renderInstruction() {
    return (
      <div className="well well-sm">
        <div>
          <p>
            Select one or more fields to use for Product / Product Family filtering in TruSales.
          </p>
          <p>
            If you cannot find the field(s) you are looking for, 
            you can also <Link to="/setup/sync-settings">add more opportunity custom fields</Link>.
          </p>
        </div>
      </div>
    );
  }

  renderSyncButton() {
    const syncing = false;
    return (
      <div>
        <span>
          {!syncing &&
            <button
              type="button"
              className="btn btn-sm btn-default"
              onClick={this.requestSyncCustomFields}
            >
              <Fa name="refresh" /> Sync custom fields with <CRMInstanceName />
            </button>}
          {syncing && <button type="button" disabled={syncing} className="btn btn-sm btn-default"><Fa name="refresh" spin /> Syncing</button>}
        </span>
      </div>
    );
  }

  filterNodes(node) {
    const { fields } = node;
    const validTypes = ['picklist', 'multipicklist'];
    return reduce(fields, (items, field) => {
      const validType = indexOf(validTypes, field.type) >= 0;
      const isReference = field.type === 'reference';
      const idType = node.objectType !== 'Opportunity' && field.is_key && field.type === 'id';
      

      const isValid = idType || isReference || validType;
      if (isValid) {
        if (idType) {
          items.push({
            ...field,
            label: node.label,
            name: `${node.objectLabel} Info`,
          });
        } else {
          items.push(field);
        }
      }
      return items;
    }, []);
  }


  renderModal() {
    const { showModal, submitting } = this.state;
    return (
      <Modal key="dynamic-product-modal" bsSize="lg" show={showModal} onHide={this.hideModal}>
          <Modal.Header closeButton>
            <h4>Add Custom Product</h4>
          </Modal.Header>
          <Modal.Body>
            <div>
              {showModal &&
                <SearchPath
                  disabled={submitting}
                  startingNode="Opportunity"
                  onUpdate={this.handleSelectedSearchPath}
                  valid_Types={['picklist', 'multipicklist', 'redefrence', 'id']}
                  filterNodes={this.filterNodes}
                />
              }
            </div>
          </Modal.Body>
          <Modal.Footer>
            <hr />
            <div className="clearfix">
              <Button className="pull-right" bsStyle="default" onClick={this.hideModal}>Done</Button>
            </div>
          </Modal.Footer>
        </Modal>
    )
  }

  renderUseStandardProduct() {
    const { useProductTable, submitting } = this.state;
    return (
      <div>
        <Toggle
          onClick={this.onToggleStandardProduct}
          on={<span>Using</span>}
          off={<span>Not Using</span>}
          size="xs"
          onstyle="success"
          offstyle="danger"
          active={useProductTable}
          disabled={submitting}
        /> <CRMInstanceName /> standard Products table.
      </div>
    );
  }

  renderTable() {
    const { fields, submitting, dirty } = this.state;

    return (
      <div>
        <div className="clearfix">
          {dirty && 
            <div className="pull-right text-right animated flash" style={{ marginBottom: '15px' }}>
              <Button bsStyle="primary" onClick={this.submit} disabled={submitting}>
                {submitting && <Fa name="spinner" spin />} Update Custom Products
              </Button>
            </div>
          }
          <div>{this.renderUseStandardProduct()}</div>
        </div>
        <p />
        <table className="table table-striped table-bordered">
          {fields.length > 0 &&
            <thead>
              <tr>
                <th>Custom Product Field</th>
                <th>Type</th>
                <th>Action</th>
              </tr>
            </thead>
          }
          <tbody>
            {fields.map((field) => {
              return (
                <tr key={field.field_id}>
                  <td>
                    {!field.accessible && <span><Fa name="exclamation-triangle" className="text-danger" />&nbsp;</span>}
                    {field.accessible && <PathDisplay startingNode="Opportunity" paths={field.path} />}
                    {!field.accessible && <PathDisplay startingNode="Opportunity" paths={field.search_paths} skipMetaCheck />}
                  </td>
                  <td>
                    {!field.accessible && <span className="label label-danger text-uppercase">inaccessible field</span>}
                    {field.accessible && <code>{field.type}</code>}
                    {field.accessible === 'reference' && <span>: <code>{field.reference_to?.join(', ')}</code></span>}
                  </td>
                  <td>
                    <ConfirmButton
                      disabled={submitting}
                      confirmMessage="Sure?"
                      confirmStyle="danger"
                      size="xs"
                      onConfirm={() => {
                        this.removeField(field);
                      }}
                    >
                      <span><Fa name="trash" /> Remove</span>
                    </ConfirmButton>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan="3">
                <Button bsStyle="default" disabled={submitting} onClick={this.showModal}><Fa name="plus" /> Add Custom Product Field</Button>
              </td>
            </tr>
          </tbody>
        </table>
        {this.renderModal()}
      </div>
    );
  }


  render() {
    const { loading, loaded } = this.state;
    const { organization } = this.props;
    const isDemo = organization && organization.is_demo;
    const orgType = organization && organization.type;
    return (
      <div>
        <Helmet title="Manage Products Integration" />
        <div className="container">
          {!isDemo && this.renderInstruction(orgType)}
          {loading && <ProgressBar />}
          {!loading && loaded &&
            <div>
              {!isDemo && this.renderTable()}
              {isDemo &&
                <div className="alert alert-warning">
                  This is a demo organization, please switch back to the original organization to change the amound field mapping.
                </div>}
            </div>}
        </div>
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state) {
  return {
    organization: getUserCurrentOrganization(state),
    viewableDimensions: getViewableDimensions(state),
    productFields: getAllProductFields(state),
  };
}

ProductPage.propTypes = {
  organization: PropTypes.shape({
    _id: PropTypes.string,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  viewableDimensions: PropTypes.shape({
    height: PropTypes.number,
  }).isRequired,
  productFields: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
    name: PropTypes.string,
  })).isRequired,
};

ProductPage.contextTypes = {
  router: PropTypes.shape({}),
  addNotification: PropTypes.func,
};

export default connect(mapStateToProps)(ProductPage);
